import { useCallback } from 'react';

import Decimal from 'decimal.js';

import { waitForTransactionReceipt } from '@wagmi/core';
import { Address } from 'viem';
import { useReadContract } from 'wagmi';

import { useUpdateVaultData } from '@services/api/vaults/vaults.api';

import { useAuth } from '@contexts/common/AuthorizationContext';

import { config } from '@tools/wagmi';

import { mapWagmiError } from '@helpers/common/error.helpers';

import { useWriteContractExtraGas } from '@utils/contract.utils';
import { applyDecimals, toBN } from '@utils/formaters.utils';

import showToast from '@components/common/Toast';

import { SuccessMessages } from '@constants/common/messages.constants';
import { ToastType } from '@constants/common/toast.constants';

import abi from '@abi/vault/ProfitStorage';

import { LegacyVaultsDto, VaultDto } from '@dto/vaults/vaults.dto';

export const useAvailableToClaim = (vault: LegacyVaultsDto) => {
  const { account } = useAuth();

  const { data, refetch, isLoading } = useReadContract({
    address: vault.profitStorage as Address,
    abi,
    functionName: 'availableToClaim',
    args: [account as Address],
    account,
    chainId: vault.chainId,
    query: {
      enabled: !!account,
    },
  });

  return {
    availableToClaim: data ? toBN(data) : toBN(0),
    refetch,
    isLoading,
  };
};

interface UseClaimProps {
  availableToClaim: Decimal;
  vault: VaultDto;
  decimals: number | undefined;
  handleUpdate: () => Promise<void>;
}

export const useClaim = ({
  availableToClaim,
  vault,
  decimals,
  handleUpdate,
}: UseClaimProps) => {
  const { account } = useAuth();
  const { mutateAsync: updateData } = useUpdateVaultData();

  const { writeContract, ...rest } = useWriteContractExtraGas({
    config,
    mutation: {
      onSuccess: async (data) => {
        const transactionReceipt = await waitForTransactionReceipt(config, {
          hash: data,
        });

        await updateData({ account: account as Address, vaultId: vault.id });
        await handleUpdate();

        showToast(
          {
            title: SuccessMessages.Claimed,
            withLink: {
              hash: transactionReceipt.transactionHash,
              chainId: vault.chainId,
            },
          },
          ToastType.Success,
        );
      },
      onError: (e) => {
        console.log('useTakeProfit:', e);
        showToast({ title: mapWagmiError(e) }, ToastType.Error);
      },
    },
  });

  const onWriteContract = useCallback(() => {
    if (!decimals) return;

    const availableToClaimValue = applyDecimals(
      availableToClaim,
      decimals,
    ).toFixed(0);

    writeContract({
      abi,
      address: vault.profitStorage as Address,
      functionName: 'claim',
      chainId: vault.chainId,
      args: [BigInt(availableToClaimValue), account as Address],
    });
  }, [account, availableToClaim, decimals, vault, writeContract]);

  return {
    writeContract: onWriteContract,
    ...rest,
  };
};
